/**
 * Version comparison for the update gate.
 *
 * Supports versions shaped like `MAJOR.MINOR.PATCH` with an optional `+BUILD`
 * suffix (e.g. `1.2.0`, `1.2.0+11`). Missing segments are treated as 0, so
 * `1.2` == `1.2.0` == `1.2.0+0`. Non-numeric/garbage segments compare as 0.
 */

function parse(version: string): number[] {
  if (!version) return [0, 0, 0, 0];
  const [core, build = '0'] = version.trim().split('+');
  const parts = core.split('.').map((p) => {
    const n = Number.parseInt(p, 10);
    return Number.isFinite(n) ? n : 0;
  });
  while (parts.length < 3) parts.push(0);
  const b = Number.parseInt(build, 10);
  parts.push(Number.isFinite(b) ? b : 0);
  return parts;
}

/** Returns -1 if a < b, 0 if equal, 1 if a > b. */
export function compareVersions(a: string, b: string): number {
  const pa = parse(a);
  const pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da < db ? -1 : 1;
  }
  return 0;
}

export type UpdateSeverity = 'ok' | 'optional' | 'required';

/**
 * Decide what the client should do given its running version and the server's
 * advertised current/minimum versions.
 *  - running < minimum  -> 'required' (force update)
 *  - running < current  -> 'optional' (prompt)
 *  - otherwise          -> 'ok'
 */
export function evaluateUpdate(
  running: string,
  current: string,
  minimum: string,
): UpdateSeverity {
  if (minimum && compareVersions(running, minimum) < 0) return 'required';
  if (current && compareVersions(running, current) < 0) return 'optional';
  return 'ok';
}
